import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ScienceIcon from '@mui/icons-material/Science';
import { useAuth } from '../services/AuthService';

const statusInfo = {
  CONNECTED: { label: 'Conectado', color: 'success' },
  CONNECTING: { label: 'Aguardando leitura do QR Code', color: 'warning' },
  DISCONNECTED: { label: 'Desconectado', color: 'default' }
};

export default function WhatsAppTestLab() {
  const { api } = useAuth();
  const [config, setConfig] = useState({ whatsapp_status: 'DISCONNECTED', whatsapp_auto_reply: false });
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [panelTab, setPanelTab] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [conversation, setConversation] = useState(null);
  const [manualText, setManualText] = useState('');
  const [monitoring, setMonitoring] = useState({ queue: {}, usage: {}, failures: [] });
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [pixPayment, setPixPayment] = useState(null);
  const pollingRef = useRef(null);

  const loadConfig = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/config');
      setConfig(response.data);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel carregar o laboratorio');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const checkStatus = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/status');
      setConfig((current) => ({
        ...current,
        whatsapp_status: response.data.status,
        whatsapp_auto_reply: response.data.auto_reply,
        whatsapp_instance_id: response.data.instance_id || current.whatsapp_instance_id
        ,whatsapp_subscription: response.data.subscription || current.whatsapp_subscription
      }));
      if (response.data.webhook_error) setError(response.data.webhook_error);
      if (response.data.status === 'CONNECTED') setQrCode('');
    } catch (_) {
      // Falhas temporarias no polling nao removem o QR Code nem a configuracao atual.
    }
  }, [api]);

  useEffect(() => {
    loadConfig();
    checkStatus();
  }, [loadConfig, checkStatus]);

  const loadConversations = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/conversations');
      setConversations(Array.isArray(response.data) ? response.data : []);
    } catch (_) {}
  }, [api]);

  const loadMonitoring = useCallback(async () => {
    try {
      const response = await api.get('/whatsapp/monitoring');
      setMonitoring(response.data || { queue: {}, usage: {}, failures: [] });
    } catch (_) {}
  }, [api]);

  const openConversation = useCallback(async (phone) => {
    setSelectedPhone(phone);
    try {
      const response = await api.get(`/whatsapp/conversations/${encodeURIComponent(phone)}/messages`);
      setConversation(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel abrir a conversa');
    }
  }, [api]);

  useEffect(() => {
    loadConversations();
    loadMonitoring();
    const interval = setInterval(() => {
      loadConversations();
      loadMonitoring();
      if (selectedPhone) openConversation(selectedPhone);
    }, 10000);
    return () => clearInterval(interval);
  }, [loadConversations, loadMonitoring, openConversation, selectedPhone]);
  useEffect(() => {
    clearInterval(pollingRef.current);
    if (config.whatsapp_status === 'CONNECTING') {
      pollingRef.current = setInterval(checkStatus, 3000);
    }
    return () => clearInterval(pollingRef.current);
  }, [checkStatus, config.whatsapp_status]);

  const connect = async () => {
    try {
      setBusy(true);
      setError('');
      const response = await api.post('/whatsapp/connect');
      setQrCode(response.data.qr_code || '');
      setConfig((current) => ({ ...current, whatsapp_status: response.data.status, whatsapp_instance_id: response.data.instance_id }));
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel gerar o QR Code');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Desconectar este numero e desligar as respostas automaticas?')) return;
    try {
      setBusy(true);
      await api.delete('/whatsapp/disconnect');
      setQrCode('');
      setConfig((current) => ({ ...current, whatsapp_status: 'DISCONNECTED', whatsapp_auto_reply: false }));
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel desconectar');
    } finally {
      setBusy(false);
    }
  };

  const toggleAutoReply = async (event) => {
    const enabled = event.target.checked;
    try {
      setBusy(true);
      const response = await api.put('/whatsapp/auto-reply', { enabled });
      setConfig((current) => ({ ...current, whatsapp_auto_reply: response.data.auto_reply }));
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel alterar a automacao');
    } finally {
      setBusy(false);
    }
  };

  const toggleConversationMode = async () => {
    if (!selectedPhone || !conversation) return;
    try {
      setBusy(true);
      const response = await api.put(`/whatsapp/conversations/${encodeURIComponent(selectedPhone)}/mode`, {
        ai_paused: !conversation.ai_paused
      });
      setConversation((current) => ({ ...current, ai_paused: response.data.ai_paused }));
      await loadConversations();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel alterar o atendimento');
    } finally {
      setBusy(false);
    }
  };

  const sendManualMessage = async () => {
    if (!selectedPhone || !manualText.trim()) return;
    try {
      setBusy(true);
      await api.post(`/whatsapp/conversations/${encodeURIComponent(selectedPhone)}/messages`, { text: manualText.trim() });
      setManualText('');
      await openConversation(selectedPhone);
      await loadConversations();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel enviar a mensagem');
    } finally {
      setBusy(false);
    }
  };

  const retryFailure = async (id) => {
    try {
      await api.post(`/whatsapp/queue/${id}/retry`);
      await loadMonitoring();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel tentar novamente');
    }
  };

  const startPayment = async (method) => {
    try {
      setBusy(true);
      setError('');
      const response = await api.post('/whatsapp/subscription/payment', { method });
      if (method === 'card') {
        window.location.href = response.data.url;
      } else {
        setPixPayment(response.data);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nao foi possivel iniciar o pagamento');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!pixPayment?.payment_id) return undefined;
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/whatsapp/subscription/payment/${pixPayment.payment_id}`);
        if (response.data.payment_status === 'approved') {
          setConfig((current) => ({ ...current, whatsapp_subscription: response.data.subscription }));
          setPixPayment(null);
          setPaymentOpen(false);
          setError('');
        }
      } catch (_) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [api, pixPayment?.payment_id]);

  if (loading) return <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  const status = statusInfo[config.whatsapp_status] || statusInfo.DISCONNECTED;
  const connected = config.whatsapp_status === 'CONNECTED';
  const subscription = config.whatsapp_subscription || {};
  const subscriptionActive = subscription.active === true;
  const canConnect = subscriptionActive || subscription.can_start_trial === true;
  const subscriptionDate = subscription.expires_at ? new Date(subscription.expires_at).toLocaleString('pt-BR') : '';

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <ScienceIcon color="primary" />
            <Typography variant="h4" fontWeight={900}>Laboratorio WhatsApp</Typography>
          </Stack>
          <Typography color="text.secondary" mt={1}>Area isolada para testar pedidos automaticos antes da liberacao em producao.</Typography>
        </Box>
        <Chip label={status.label} color={status.color} sx={{ fontWeight: 900, alignSelf: 'flex-start' }} />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Alert severity={subscriptionActive ? (subscription.status === 'trialing' ? 'info' : 'success') : 'error'} sx={{ mb: 2 }}
        action={<Button color="inherit" variant="outlined" onClick={() => setPaymentOpen(true)}>Pagar R$ 35</Button>}>
        {subscriptionActive
          ? subscription.status === 'trialing'
            ? `Teste gratis ativo por somente 2 dias. Termina em ${subscriptionDate}. Depois, o acesso custa R$ 35 por mes.`
            : `WhatsApp com IA ativo ate ${subscriptionDate}. Mensalidade: R$ 35.`
          : subscription.can_start_trial
            ? 'Conecte seu WhatsApp para iniciar os 2 dias gratis. O teste so comeca no primeiro uso.'
            : 'Seu acesso ao WhatsApp com IA esta vencido. O numero foi desconectado. Pague R$ 35 para liberar por 1 mes.'}
      </Alert>
      <Alert severity="warning" sx={{ mb: 3 }}>
        O modulo inicia com respostas automaticas desligadas. Confira o cardapio e faca pedidos de teste antes de ativar.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr .9fr' }, gap: 3 }}>
        <Card variant="outlined" sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" gap={1.5} mb={2}>
              <WhatsAppIcon sx={{ color: '#16a34a', fontSize: 34 }} />
              <Box>
                <Typography variant="h6" fontWeight={900}>WhatsApp da lanchonete</Typography>
                <Typography variant="body2" color="text.secondary">Cada loja possui sua propria instancia.</Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {config.whatsapp_instance_id && (
              <Typography variant="body2" color="text.secondary" mb={2}>Instancia: <strong>{config.whatsapp_instance_id}</strong></Typography>
            )}
            {!connected ? (
              <Button fullWidth size="large" variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />} onClick={connect} disabled={busy || !canConnect}>
                {config.whatsapp_status === 'CONNECTING' ? 'Gerar novo QR Code' : 'Conectar WhatsApp da Lanchonete'}
              </Button>
            ) : (
              <Button fullWidth size="large" color="error" variant="outlined" startIcon={<LinkOffIcon />} onClick={disconnect} disabled={busy}>Desconectar WhatsApp</Button>
            )}

            <Paper variant="outlined" sx={{ mt: 3, p: 2, borderRadius: 3, bgcolor: '#f8fafc' }}>
              <FormControlLabel
                control={<Switch checked={config.whatsapp_auto_reply === true} onChange={toggleAutoReply} disabled={!connected || busy || !subscriptionActive} />}
                label={<Box><Typography fontWeight={900}>Resposta automatica</Typography><Typography variant="body2" color="text.secondary">Permite que a IA interprete e grave pedidos.</Typography></Box>}
              />
              {!connected && <Typography variant="caption" color="warning.main">Conecte o WhatsApp antes de ativar.</Typography>}
            </Paper>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <QrCode2Icon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={900} mt={1}>QR Code de conexao</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>WhatsApp &gt; Aparelhos conectados &gt; Conectar aparelho</Typography>
            {qrCode ? (
              <Box component="img" src={qrCode} alt="QR Code para conectar o WhatsApp" sx={{ width: '100%', maxWidth: 290, aspectRatio: '1', objectFit: 'contain', bgcolor: '#fff', p: 1, borderRadius: 2 }} />
            ) : (
              <Box sx={{ minHeight: 290, display: 'grid', placeItems: 'center', bgcolor: '#f8fafc', borderRadius: 3, border: '1px dashed #cbd5e1' }}>
                <Typography color="text.secondary">{connected ? 'WhatsApp conectado com sucesso.' : 'Clique em conectar para gerar o QR Code.'}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Paper variant="outlined" sx={{ mt: 3, borderRadius: 4, overflow: 'hidden' }}>
        <Tabs value={panelTab} onChange={(_, value) => setPanelTab(value)} sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab label={`Conversas (${conversations.length})`} />
          <Tab label="Monitoramento e custos" />
        </Tabs>

        {panelTab === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, minHeight: 520 }}>
            <Box sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider', maxHeight: 620, overflow: 'auto' }}>
              {conversations.length === 0 && <Typography color="text.secondary" p={3}>Nenhuma conversa registrada ainda.</Typography>}
              {conversations.map((item) => (
                <Box
                  component="button"
                  type="button"
                  key={item.customer_phone}
                  onClick={() => openConversation(item.customer_phone)}
                  sx={{ width: '100%', border: 0, borderBottom: '1px solid #e5e7eb', bgcolor: selectedPhone === item.customer_phone ? '#f0fdf4' : '#fff', p: 2, textAlign: 'left', cursor: 'pointer' }}
                >
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography fontWeight={900}>{item.customer_name || `+${item.customer_phone}`}</Typography>
                    <Chip size="small" color={item.ai_paused ? 'warning' : 'success'} label={item.ai_paused ? 'Humano' : 'IA'} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" noWrap>{item.last_message || 'Sem mensagem'}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {!conversation ? (
                <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}><Typography color="text.secondary">Selecione uma conversa.</Typography></Box>
              ) : (
                <>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2} mb={2}>
                    <Box><Typography variant="h6" fontWeight={900}>+{conversation.phone}</Typography><Typography variant="body2" color="text.secondary">Histórico preservado mesmo após concluir o pedido</Typography></Box>
                    <Button color={conversation.ai_paused ? 'success' : 'warning'} variant="contained" onClick={toggleConversationMode} disabled={busy}>
                      {conversation.ai_paused ? 'Devolver para a IA' : 'Pausar IA e assumir'}
                    </Button>
                  </Stack>
                  <Box sx={{ flex: 1, minHeight: 320, maxHeight: 430, overflow: 'auto', bgcolor: '#f8fafc', borderRadius: 3, p: 2 }}>
                    {(conversation.messages || []).map((message) => (
                      <Box key={message.id} sx={{ display: 'flex', justifyContent: message.direction === 'IN' ? 'flex-start' : 'flex-end', mb: 1.5 }}>
                        <Paper sx={{ maxWidth: '82%', p: 1.5, bgcolor: message.direction === 'IN' ? '#fff' : message.sender_type === 'HUMAN' ? '#dbeafe' : '#dcfce7' }}>
                          <Typography variant="caption" fontWeight={900}>{message.direction === 'IN' ? 'Cliente' : message.sender_type === 'HUMAN' ? 'Atendente' : 'IA'}</Typography>
                          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.content}</Typography>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} mt={2}>
                    <TextField fullWidth multiline maxRows={4} placeholder="Escreva como atendente..." value={manualText} onChange={(event) => setManualText(event.target.value)} />
                    <Button variant="contained" onClick={sendManualMessage} disabled={busy || !manualText.trim()}>Enviar</Button>
                  </Stack>
                  {!conversation.ai_paused && <Typography variant="caption" color="warning.main" mt={1}>Ao enviar manualmente, a IA sera pausada automaticamente nesta conversa.</Typography>}
                </>
              )}
            </Box>
          </Box>
        )}

        {panelTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={900} mb={2}>Últimos 30 dias</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
              {[
                ['Requisições de IA', monitoring.usage?.requests || 0],
                ['Custo estimado', `US$ ${Number(monitoring.usage?.estimated_cost_usd || 0).toFixed(4)}`],
                ['Tempo médio', `${monitoring.usage?.average_latency_ms || 0} ms`],
                ['Na fila', (monitoring.queue?.PENDING || 0) + (monitoring.queue?.RETRY || 0)],
                ['Falhas finais', monitoring.queue?.FAILED || 0]
              ].map(([label, value]) => <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }} key={label}><Typography variant="body2" color="text.secondary">{label}</Typography><Typography variant="h6" fontWeight={900}>{value}</Typography></Paper>)}
            </Box>
            <Typography fontWeight={900} mb={1}>Falhas que precisam de atenção</Typography>
            {(monitoring.failures || []).length === 0 ? <Alert severity="success">Nenhuma falha final registrada.</Alert> : (
              <Stack gap={1}>
                {monitoring.failures.map((failure) => (
                  <Paper variant="outlined" sx={{ p: 2 }} key={failure.id}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                      <Box><Typography fontWeight={900}>+{failure.customer_phone} — {failure.attempts} tentativas</Typography><Typography variant="body2" color="error.main">{failure.last_error}</Typography></Box>
                      <Button variant="outlined" onClick={() => retryFailure(failure.id)}>Tentar novamente</Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>WhatsApp com IA — R$ 35 por mes</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>O pagamento libera o recurso por 1 mes. Sem pagamento, o WhatsApp e desconectado automaticamente.</Alert>
          {pixPayment ? <Stack alignItems="center" gap={2}>
            {pixPayment.qr_code_base64 && <Box component="img" alt="QR Code PIX" src={`data:image/png;base64,${pixPayment.qr_code_base64}`} sx={{ width: 260, maxWidth: '100%' }} />}
            <TextField fullWidth label="PIX copia e cola" value={pixPayment.qr_code || ''} InputProps={{ readOnly: true }} />
            <Button onClick={() => navigator.clipboard.writeText(pixPayment.qr_code || '')}>Copiar codigo PIX</Button>
            <Typography color="text.secondary">Aguardando confirmacao do Mercado Pago...</Typography>
          </Stack> : <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <Button fullWidth variant="contained" onClick={() => startPayment('pix')} disabled={busy}>Pagar com PIX</Button>
            <Button fullWidth variant="outlined" onClick={() => startPayment('card')} disabled={busy}>Pagar com cartao</Button>
          </Stack>}
        </DialogContent>
        <DialogActions><Button onClick={() => { setPaymentOpen(false); setPixPayment(null); }}>Fechar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
